import React from 'react';
import classnames from 'classnames/bind';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToggle } from '@mantine/hooks';

import { DEPLOYMENT_STATES, DeploymentState } from 'types/deployment';
import Pipeline from 'types/pipeline';
import { Gateway } from 'types/gateway';

import { useHasAccess } from 'hooks/useHasAccess';

import { Button } from 'components/Button/Button';
import { DeploymentList } from 'deployments/components/List/List';
import { Drawer } from 'components/Drawer/DrawerDeprecated';
import { GatewaySelect } from 'gateways/components/GatewaySelect/GatewaySelect';
import { Loader } from 'components/Loader/Loader';
import {
  deploymentFormDrawerClassName,
  NewDeploymentForm,
} from 'deployments/components/Form';
import { PipelineSelect } from 'pipelines/components/PipelineSelect/PipelineSelect';
import { StateSelect } from 'components/StateSelect/StateSelect';

import styles from './DeploymentListSmall.module.scss';
import {
  useDeployments,
  UseDeploymentsParams,
} from 'deployments/hooks/useDeployments';

const c = classnames.bind(styles);

export type DeploymentListSmallProps = {
  cameraId?: string;
  streamIds?: (string | undefined)[];
  gateway?: Gateway;
  hideDeployButton?: boolean;
};

export function DeploymentListSmall({
  cameraId,
  streamIds,
  gateway,
  hideDeployButton = false,
}: DeploymentListSmallProps) {
  const [hasAccess] = useHasAccess();

  const navigate = useNavigate();
  const { search } = useLocation();

  const allowedStreamIds = streamIds
    ? (streamIds.filter((element) => element !== undefined) as string[])
    : [];
  const allowedCameraIds = cameraId ? [cameraId] : [];

  const [queryFilters, setQueryFilters] = React.useState<
    UseDeploymentsParams & { page: number }
  >({ page: 1 });
  const queryResult = useDeployments({
    params: {
      video_source_ids: [...allowedStreamIds, ...allowedCameraIds],
      gateway_ids: gateway ? [gateway.id] : [],
      ...queryFilters,
    },
    keepPreviousData: true,
  });

  const [isDeploying, toggleIsDeploying] = useToggle();

  function handleStateFilterChange(state: string | null) {
    setQueryFilters((previousFilters) => ({
      ...previousFilters,
      states: state ? [state as DeploymentState] : undefined,
      page: 1,
    }));
  }

  function handleGatewayFilterChange(gateway: Gateway | null) {
    setQueryFilters((previousFilters) => ({
      ...previousFilters,
      gateway_ids: gateway ? [gateway.id] : undefined,
      page: 1,
    }));
  }

  function handlePipelineFilterChange(pipeline: Pipeline | null) {
    setQueryFilters((previousFilters) => ({
      ...previousFilters,
      pipeline_ids: pipeline ? [pipeline.id] : undefined,
      page: 1,
    }));
  }

  function handlePageChange(page: number) {
    setQueryFilters((previousFilters) => ({
      ...previousFilters,
      page,
    }));
  }

  if (queryResult.isLoading) {
    return <Loader text="Loading deployments..." />;
  }

  return (
    <div className={c('wrap')}>
      <div className={c('top')}>
        <div className={c('filter')}>
          <StateSelect
            className={c('filter-dropdown')}
            onChange={handleStateFilterChange}
            options={DEPLOYMENT_STATES}
            size="small"
          />
          <PipelineSelect
            className={c('filter-dropdown')}
            onChange={handlePipelineFilterChange}
            size="small"
          />
          <GatewaySelect
            className={c('filter-dropdown')}
            onChange={handleGatewayFilterChange}
            size="small"
          />
        </div>
        <div>{queryResult?.data?.total_elements} Workflows</div>
        <>
          <div className={c('spacer')}></div>

          {hasAccess('deploy_edit') && !hideDeployButton && (
            <Button
              onClick={() => toggleIsDeploying()}
              variant="primary"
              size="small"
            >
              Deploy a Workflow
            </Button>
          )}
        </>
      </div>
      <DeploymentList
        queryResult={queryResult}
        page={queryFilters.page}
        onPageChange={handlePageChange}
        compact
      />
      <Drawer
        title="Deploy workflow"
        containerClassName={deploymentFormDrawerClassName}
        onClose={toggleIsDeploying}
        open={isDeploying}
      >
        <NewDeploymentForm
          onSuccess={({ id }) => {
            toggleIsDeploying(false);
            navigate({ pathname: `./ref/${id}`, search });
          }}
          allowedStreamIds={allowedStreamIds}
          allowedCameraIds={allowedCameraIds}
          gateway={gateway}
        />
      </Drawer>
    </div>
  );
}
